import { useState, type FormEvent } from "react";
import { useLocale } from "./localization";
import {
  validDisplayNames,
  type ConferenceDashboardClient,
  type ConferenceDetail,
  type ConferenceDisplayNames,
} from "./portfolio-overview-api";

export function ConferenceNamesForm({
  client,
  conference,
  onSaved,
}: {
  readonly client: ConferenceDashboardClient;
  readonly conference: ConferenceDetail;
  readonly onSaved: (names: ConferenceDisplayNames) => void;
}) {
  const { t } = useLocale();
  const [ar, setAr] = useState(conference.displayNames?.ar ?? "");
  const [en, setEn] = useState(conference.displayNames?.en ?? conference.name);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<"idle" | "saved" | "error" | "invalid">(
    "idle",
  );
  const names = { ar: ar.trim(), en: en.trim() };
  const dirty =
    names.ar !== (conference.displayNames?.ar ?? "") ||
    names.en !== (conference.displayNames?.en ?? conference.name);

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) return;
    if (!validDisplayNames(names)) {
      setStatus("invalid");
      return;
    }
    if (!client.updateConferenceNames) {
      setStatus("error");
      return;
    }
    setBusy(true);
    setStatus("idle");
    try {
      const result = await client.updateConferenceNames(
        conference.conferenceId,
        names,
      );
      setAr(result.ar);
      setEn(result.en);
      onSaved(result);
      setStatus("saved");
    } catch {
      setStatus("error");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section
      className="detail-panel conference-names"
      aria-labelledby="conference-names-title"
    >
      <h2 id="conference-names-title">{t("conferenceEditor.title")}</h2>
      <p>{t("conferenceEditor.description")}</p>
      <form onSubmit={save}>
        <fieldset disabled={busy}>
          <div className="conference-names__fields">
            <label htmlFor="conference-name-ar">
              {t("conferenceEditor.ar")}
              <input
                id="conference-name-ar"
                name="nameAr"
                dir="rtl"
                lang="ar"
                required
                maxLength={160}
                value={ar}
                onChange={(event) => {
                  setAr(event.target.value);
                  setStatus("idle");
                }}
              />
            </label>
            <label htmlFor="conference-name-en">
              {t("conferenceEditor.en")}
              <input
                id="conference-name-en"
                name="nameEn"
                dir="ltr"
                lang="en"
                required
                maxLength={160}
                value={en}
                onChange={(event) => {
                  setEn(event.target.value);
                  setStatus("idle");
                }}
              />
            </label>
          </div>
          <div
            className="conference-names__preview"
            aria-label={t("conferenceEditor.preview")}
          >
            <span lang="ar" dir="rtl">
              <small>{t("conferenceEditor.ar")}</small>
              <bdi>{ar.trim() || conference.name}</bdi>
            </span>
            <span lang="en" dir="ltr">
              <small>{t("conferenceEditor.en")}</small>
              <bdi>{en.trim() || conference.name}</bdi>
            </span>
          </div>
          <div className="conference-names__actions">
            <button
              className="button button--primary"
              type="submit"
              disabled={!dirty || !client.updateConferenceNames}
            >
              {t(busy ? "conferenceEditor.saving" : "conferenceEditor.save")}
            </button>
            <button
              className="button button--secondary"
              type="button"
              disabled={!dirty}
              onClick={() => {
                setAr(conference.displayNames?.ar ?? "");
                setEn(conference.displayNames?.en ?? conference.name);
                setStatus("idle");
              }}
            >
              {t("conferenceEditor.cancel")}
            </button>
          </div>
        </fieldset>
        <p
          role={status === "error" || status === "invalid" ? "alert" : "status"}
        >
          {status !== "idle"
            ? t(`conferenceEditor.${status}`)
            : dirty
              ? t("conferenceEditor.unsaved")
              : ""}
        </p>
      </form>
    </section>
  );
}
